import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Collapse,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  Typography,
  Card,
  CardContent,
  Autocomplete,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import axios from "axios";
import { BASE_URL } from "../../../components/config/config";
import { State } from "react-country-state-city";

const CentersFilter = ({
  onFilterSubmit,
  onClearFilters,
  primaryServices = [],
  secondaryServices = [],
  statesList = [],
  initialFilters = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState({
    center_name: "",
    phone_number: "",
    primary_services: "",
    pincode: "",
    is_otp_verfied: "",
    qr_id: "",
    city: "",
    start_date: "",
    end_date: "",
    paid_for_onboarding_kit: "",
    referral_received: "",
    client_obj_id: "",
    is_active: "",
    state: "",
    ...initialFilters,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [apiPrimaryServices, setApiPrimaryServices] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [availableStates, setAvailableStates] = useState([]);

  const primaryServicesFetchedRef = useRef(false);
  const clientsFetchedRef = useRef(false);

  // Sync filters when initialFilters prop changes
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setFilters((prev) => {
        // Merge with existing filters, ensuring all fields are present
        const merged = {
          center_name: "",
          phone_number: "",
          primary_services: "",
          pincode: "",
          is_otp_verfied: "",
          qr_id: "",
          city: "",
          start_date: "",
          end_date: "",
          paid_for_onboarding_kit: "",
          referral_received: "",
          client_obj_id: "",
          is_active: "",
          state: "",
          ...prev,
          ...initialFilters,
        };
        return merged;
      });
    }
  }, [initialFilters]);

  const fetchPrimaryServices = async () => {
    if (primaryServicesFetchedRef.current) return;

    primaryServicesFetchedRef.current = true;

    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(
        `${BASE_URL}admin/get-all-active-services`,
        {
          headers: {
            "x-access-token": accessToken,
          },
        }
      );

      if (response.data.success && response.data.data) {
        const services = response.data.data.map((service) => ({
          id: service._id,
          name: service.service_name,
          sequence: service.sequence_order,
          isActive: service.is_active,
        }));
        setApiPrimaryServices(services);
      }
    } catch (error) {
      console.error("Error fetching primary services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsList = async () => {
    if (clientsFetchedRef.current) return;

    clientsFetchedRef.current = true;

    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(`${BASE_URL}admin/get-all-clients`, {
        headers: {
          "x-access-token": accessToken,
        },
      });

      if (response.data && response.data.data) {
        const clients = response.data.data.map((client) => ({
          id: client._id,
          name: client.client_name,
        }));
        setClientsList(clients);
      }
    } catch (error) {
      console.error("Error fetching clients list:", error);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchPrimaryServices();
      await fetchClientsList();
      
      // Load states from react-country-state-city (India country code: IN)
      try {
        if (State && typeof State.getStatesOfCountry === "function") {
          const indiaStates = State.getStatesOfCountry("IN");
          if (indiaStates && Array.isArray(indiaStates)) {
            const statesFormatted = indiaStates.map((state) => ({
              value: state.name,
              label: state.name,
              isoCode: state.isoCode,
            }));
            setAvailableStates(statesFormatted);
          } else {
            setAvailableStates([]);
          }
        } else {
          console.warn("State.getStatesOfCountry is not available");
          setAvailableStates([]);
        }
      } catch (error) {
        console.error("Error loading states:", error);
        setAvailableStates([]);
      }
    };

    loadInitialData();
  }, []);

  // Set default date range (one week)
  const setDefaultDateRange = () => {
    const oneWeekAgo = moment().subtract(7, "days").format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");
    setFilters((prev) => ({
      ...prev,
      start_date: oneWeekAgo,
      end_date: today,
    }));
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target ? event.target.value : event;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelectChange = (field) => (event, newValue) => {
    const value = Array.isArray(newValue)
      ? newValue.map((v) => (typeof v === "string" ? v : v.value)).join(",")
      : "";
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (field) => (date) => {
    const value = date ? moment(date).format("YYYY-MM-DD") : "";
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Remove empty filters except date filters
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => {
          // Include state filter even if it's a comma-separated string
          if (key === "state" && value && value.trim() !== "") {
            return true;
          }
          return (
            value !== "" &&
            value !== null &&
            value !== undefined &&
            (key === "start_date" || key === "end_date" || value !== "")
          );
        }
      )
    );

    onFilterSubmit(activeFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      center_name: "",
      phone_number: "",
      primary_services: "",
      pincode: "",
      is_otp_verfied: "",
      qr_id: "",
      city: "",
      start_date: "",
      end_date: "",
      paid_for_onboarding_kit: "",
      referral_received: "",
      client_obj_id: "",
      is_active: "",
      state: "",
    };

    setFilters(emptyFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== "" && value !== null && value !== undefined
  );

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== null && value !== undefined
    ).length;
  };

  // Helper function to get service name by ID for display
  const getServiceNameById = (serviceId) => {
    const service = apiPrimaryServices.find((s) => s.id === serviceId);
    return service ? service.name : serviceId;
  };

  // Helper function to get client name by ID for display
  const getClientNameById = (clientId) => {
    const client = clientsList.find((c) => c.id === clientId);
    return client ? client.name : clientId;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Card
        elevation={1}
        sx={{
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          {/* Header */}
          <Box
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: filtersOpen ? 2 : 0,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "action.hover",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                mt: -0.5,
                mb: filtersOpen ? 1.5 : -0.5,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setFiltersOpen(!filtersOpen);
                }}
                size="small"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Filters
                </Typography>
                {hasActiveFilters && (
                  <Typography variant="caption" color="text.secondary">
                    {getActiveFilterCount()} active filter(s)
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {hasActiveFilters && (
                <Chip
                  label={`${getActiveFilterCount()} active`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setFiltersOpen(!filtersOpen);
                }}
                size="small"
                sx={{ color: theme.palette.text.secondary }}
              >
                {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {/* Filters Form */}
          <Collapse in={filtersOpen}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={
                      filters.start_date ? moment(filters.start_date) : null
                    }
                    onChange={handleDateChange("start_date")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="End Date"
                    value={filters.end_date ? moment(filters.end_date) : null}
                    onChange={handleDateChange("end_date")}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Center Name"
                    value={filters.center_name}
                    onChange={handleFilterChange("center_name")}
                    size="small"
                    variant="outlined"
                    placeholder="Search by center name..."
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={filters.phone_number}
                    onChange={handleFilterChange("phone_number")}
                    size="small"
                    variant="outlined"
                    type="tel"
                    placeholder="Enter phone number"
                  />
                </Grid>

                {/* Second Row */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Primary Service</InputLabel>
                    <Select
                      value={filters.primary_services}
                      label="Primary Service"
                      onChange={handleFilterChange("primary_services")}
                    >
                      <MenuItem value="">
                        <em>All Services</em>
                      </MenuItem>
                      {apiPrimaryServices
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((service) => (
                          <MenuItem key={service.id} value={service.id}>
                            {service.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={filters.pincode}
                    onChange={handleFilterChange("pincode")}
                    size="small"
                    variant="outlined"
                    placeholder="Enter pincode"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>OTP Verification</InputLabel>
                    <Select
                      value={filters.is_otp_verfied}
                      label="OTP Verification"
                      onChange={handleFilterChange("is_otp_verfied")}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="true">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "success.main",
                            }}
                          />
                          Verified
                        </Box>
                      </MenuItem>
                      <MenuItem value="false">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "error.main",
                            }}
                          />
                          Not Verified
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="QR ID"
                    value={filters.qr_id}
                    onChange={handleFilterChange("qr_id")}
                    size="small"
                    variant="outlined"
                    placeholder="Enter QR ID"
                  />
                </Grid>

                {/* State Filter with Multiselect */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <Autocomplete
                    multiple
                    options={availableStates}
                    getOptionLabel={(option) => option.label || option}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    value={
                      filters.state && filters.state !== ""
                        ? filters.state
                            .split(",")
                            .filter((v) => v.trim() !== "")
                            .map((val) => {
                              const option = (availableStates || []).find(
                                (opt) => opt.value === val.trim()
                              );
                              return (
                                option || { value: val.trim(), label: val.trim() }
                              );
                            })
                        : []
                    }
                    onChange={(event, newValue) => {
                      const value =
                        newValue && newValue.length > 0
                          ? newValue.map((v) => v.value).join(",")
                          : "";
                      setFilters((prev) => ({
                        ...prev,
                        state: value,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        size="small"
                        variant="outlined"
                        placeholder="Select states"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.label || option}
                          {...getTagProps({ index })}
                          key={option.value || index}
                          size="small"
                        />
                      ))
                    }
                  />
                </Grid>

                {/* Third Row - New Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="City"
                    value={filters.city}
                    onChange={handleFilterChange("city")}
                    size="small"
                    variant="outlined"
                    placeholder="Enter city"
                  />
                </Grid>

                {/* New Filter: Subscribed/Not Subscribed */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Subscription Status</InputLabel>
                    <Select
                      value={filters.paid_for_onboarding_kit}
                      label="Subscription Status"
                      onChange={handleFilterChange("paid_for_onboarding_kit")}
                    >
                      <MenuItem value="">All Centers</MenuItem>
                      <MenuItem value="true">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "success.main",
                            }}
                          />
                          Subscribed
                        </Box>
                      </MenuItem>
                      <MenuItem value="false">
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "warning.main",
                            }}
                          />
                          Not Subscribed
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* New Filter: Onboarded By */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Onboarded By"
                    value={filters.referral_received}
                    onChange={handleFilterChange("referral_received")}
                    size="small"
                    variant="outlined"
                    placeholder="Enter referral code"
                  />
                </Grid>

                {/* New Filter: Authorized For Clients */}
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Authorized For Client</InputLabel>
                    <Select
                      value={filters.client_obj_id}
                      label="Authorized For Client"
                      onChange={handleFilterChange("client_obj_id")}
                    >
                      <MenuItem value="">
                        <em>All Clients</em>
                      </MenuItem>
                      {clientsList.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      onClick={handleClearFilters}
                      variant="outlined"
                      color="secondary"
                      size="medium"
                      startIcon={<RefreshIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Clear All
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="medium"
                      startIcon={<SearchIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Grid>

                {/* Active Filters Display */}
                {hasActiveFilters && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mr: 1 }}
                      >
                        Active filters:
                      </Typography>
                      {Object.entries(filters).map(([key, value]) => {
                        if (!value || value === "") return null;

                        let displayValue = value;
                        if (key === "is_otp_verfied") {
                          displayValue =
                            value === "true"
                              ? "OTP Verified"
                              : "OTP Not Verified";
                        } else if (key === "start_date" || key === "end_date") {
                          displayValue = moment(value).format("MMM D, YYYY");
                        } else if (key === "primary_services") {
                          displayValue = getServiceNameById(value);
                        } else if (key === "paid_for_onboarding_kit") {
                          displayValue =
                            value === "true" ? "Subscribed" : "Not Subscribed";
                        } else if (key === "client_obj_id") {
                          displayValue = getClientNameById(value);
                        } else if (key === "is_active") {
                          const stateMap = {
                            "1": "ACTIVE",
                            "2": "DRAFTED",
                            "-1": "IN_ACTIVE",
                            "0": "DELETED",
                          };
                          displayValue = value
                            .split(",")
                            .map((v) => stateMap[v.trim()] || v.trim())
                            .join(", ");
                        } else if (key === "state") {
                          displayValue = value
                            .split(",")
                            .map((v) => v.trim())
                            .join(", ");
                        }

                        return (
                          <Chip
                            key={key}
                            label={`${key.replace(/_/g, " ")}: ${displayValue}`}
                            size="medium"
                            onDelete={() =>
                              handleFilterChange(key)({ target: { value: "" } })
                            }
                            color="primary"
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </form>
          </Collapse>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
};

export default CentersFilter;
